import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { interpret } from "xstate";
import ClientDetailsMixin from "../../ClientDetailsMixin";
import UnsavedChangesMixin from "@/router/views/UnsavedChangesMixin";
import ClientModule from "@/store/modules/client.module";
import teamCorrespondenceMachine from "../../machines/TeamCorrespondenceMachine";
import { TEAM_CORRESPONDENCE_ROLES, teamCorrespondenceHasAnyRole, } from "@/store/models/client/teamCorrespondence";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
const NO_ROLES = { isConnectedContact: false, isPerformanceApprover: false, isPrimaryLeaveApprover: false, isSecondaryLeaveApprover: false };
// Primary and Secondary leave can't both be on for the same contact
const EXCLUSIVE_ROLE = {
    isPrimaryLeaveApprover: "isSecondaryLeaveApprover",
    isSecondaryLeaveApprover: "isPrimaryLeaveApprover",
};
/**
 * Team detail > Correspondence: pick a team member, then set their roles for each client contact (Associated contact,
 * Appraisal reviewer, Primary leave approver, Secondary leave approver). Edits for every member are kept in a working copy and saved
 * together; Cancel, switching tab and leaving the page ask before throwing them away (UnsavedChangesMixin).
 */
let TeamCorrespondenceComponent = /** @class */ (() => {
    let TeamCorrespondenceComponent = class TeamCorrespondenceComponent extends Mixins(ClientDetailsMixin, UnsavedChangesMixin) {
        constructor() {
            super(...arguments);
            this.roles = TEAM_CORRESPONDENCE_ROLES;
            this.contactFilters = [
                { key: "all", label: "All" },
                { key: "assigned", label: "Assigned" },
                { key: "notAssigned", label: "Not assigned" },
            ];
            this.machine = teamCorrespondenceMachine;
            this.machineService = interpret(this.machine, { devTools: true });
            this.state = this.machine.initialState;
            this.cur = {};
            this.selectedMemberId = null;
            this.memberSearch = "";
            this.contactSearch = "";
            this.contactFilter = "all";
        }
        get correspondence() {
            return ClientModule.teamCorrespondence;
        }
        get isLoading() {
            return this.state.matches("loading") && !this.correspondence;
        }
        get isSaving() {
            return this.state.matches("saving");
        }
        // Saving, or reloading after a save - edits made now would be overwritten by the reload
        get isBusy() {
            return this.isSaving || this.state.matches("loading");
        }
        get canEdit() {
            return !!this.correspondence && this.correspondence.isTeamActive;
        }
        get members() {
            const members = this.correspondence ? this.correspondence.members : [];
            const search = this.memberSearch.trim().toLowerCase();
            return search ? members.filter((m) => m.name.toLowerCase().includes(search)) : members;
        }
        get selectedMember() {
            return this.correspondence ? this.correspondence.members.find((m) => m.teamMemberId === this.selectedMemberId) || null : null;
        }
        get contacts() {
            const memberRoles = this.selectedMemberId ? this.cur[this.selectedMemberId] : null;
            if (!this.correspondence || !memberRoles) {
                return [];
            }
            const search = this.contactSearch.trim().toLowerCase();
            return this.correspondence.contacts.filter((contact) => {
                const assigned = this.hasAnyRole(memberRoles[contact.id]);
                if ((this.contactFilter === "assigned" && !assigned) || (this.contactFilter === "notAssigned" && assigned)) {
                    return false;
                }
                return !search || contact.name.toLowerCase().includes(search) || (contact.designation || "").toLowerCase().includes(search);
            });
        }
        // Every member x contact that differs from what is saved, across all members
        get changedCells() {
            if (!this.correspondence) {
                return [];
            }
            const saved = this.savedRoles();
            const changed = [];
            Object.keys(this.cur).forEach((teamMemberId) => Object.keys(this.cur[teamMemberId]).forEach((clientContactId) => {
                const roles = this.cur[teamMemberId][clientContactId];
                const before = (saved[teamMemberId] && saved[teamMemberId][clientContactId]) || NO_ROLES;
                if (this.roles.some((role) => roles[role.key] !== before[role.key])) {
                    changed.push({ teamMemberId, clientContactId, ...roles });
                }
            }));
            return changed;
        }
        get hasChanges() {
            return this.changedCells.length > 0;
        }
        // The values that count as unsaved edits (see UnsavedChangesMixin)
        unsavedState() {
            return this.cur;
        }
        send(event) {
            this.machineService.send(event);
        }
        // Contacts with at least one role on, from the working copy so it updates as pills change
        contactCount(teamMemberId) {
            const memberRoles = this.cur[teamMemberId] || {};
            return Object.keys(memberRoles).filter((contactId) => this.hasAnyRole(memberRoles[contactId])).length;
        }
        isOn(contactId, role) {
            const memberRoles = this.selectedMemberId ? this.cur[this.selectedMemberId] : null;
            return !!memberRoles && !!memberRoles[contactId] && memberRoles[contactId][role];
        }
        isRoleDisabled(contactId, role) {
            const other = EXCLUSIVE_ROLE[role];
            return !this.canEdit || this.isBusy || (!!other && this.isOn(contactId, other));
        }
        roleTitle(contactId, role) {
            const other = EXCLUSIVE_ROLE[role];
            return other && this.isOn(contactId, other) ? "The same contact can't be both the primary and the secondary leave approver." : "";
        }
        toggleRole(contactId, role) {
            if (!this.selectedMemberId || this.isRoleDisabled(contactId, role)) {
                return;
            }
            const roles = this.cur[this.selectedMemberId][contactId];
            roles[role] = !roles[role];
        }
        selectMember(teamMemberId) {
            this.selectedMemberId = teamMemberId;
        }
        save() {
            if (!this.hasChanges || this.isBusy) {
                return;
            }
            this.send({ type: "SAVE", payLoad: { teamId: this.teamId, cells: this.changedCells } });
        }
        cancel() {
            this.confirmLeave().then((leave) => {
                if (leave) {
                    this.resetWorkingCopy();
                }
            });
        }
        hasAnyRole(roles) {
            return !!roles && teamCorrespondenceHasAnyRole(roles);
        }
        savedRoles() {
            const saved = {};
            (this.correspondence ? this.correspondence.cells : []).forEach((cell) => {
                saved[cell.teamMemberId] = saved[cell.teamMemberId] || {};
                saved[cell.teamMemberId][cell.clientContactId] = {
                    isConnectedContact: cell.isConnectedContact,
                    isPerformanceApprover: cell.isPerformanceApprover,
                    isPrimaryLeaveApprover: cell.isPrimaryLeaveApprover,
                    isSecondaryLeaveApprover: cell.isSecondaryLeaveApprover,
                };
            });
            return saved;
        }
        // Every member x contact, from the saved cells; the snapshot for unsaved changes is taken here
        resetWorkingCopy() {
            const data = this.correspondence;
            const saved = this.savedRoles();
            const cur = {};
            (data ? data.members : []).forEach((member) => {
                cur[member.teamMemberId] = {};
                data.contacts.forEach((contact) => {
                    const roles = saved[member.teamMemberId] && saved[member.teamMemberId][contact.id];
                    cur[member.teamMemberId][contact.id] = { ...(roles || NO_ROLES) };
                });
            });
            this.cur = cur;
            this.markFormClean();
        }
        // A (re)load arrived: start from what is saved and keep the selected member when it is still there
        onCorrespondenceLoaded() {
            if (!this.correspondence) {
                return;
            }
            this.resetWorkingCopy();
            const members = this.correspondence.members;
            if (!members.some((m) => m.teamMemberId === this.selectedMemberId)) {
                this.selectedMemberId = members.length ? members[0].teamMemberId : null;
            }
        }
        startMachine() {
            this.machineService.stop();
            ClientModule.SetTeamCorrespondence(null);
            this.cur = {};
            this.selectedMemberId = null;
            this.machineService = interpret(this.machine.withContext({ ...this.machine.context, teamId: this.teamId }), { devTools: true });
            this.machineService.onTransition((state) => {
                this.state = state;
            });
            this.machineService.start();
        }
        // A different team opened while this tab stays mounted
        onTeamChange() {
            this.startMachine();
        }
        created() {
            this.startMachine();
        }
        beforeDestroy() {
            this.machineService.stop();
            ClientModule.SetTeamCorrespondence(null);
        }
    };
    __decorate([
        Prop({ required: true }),
        __metadata("design:type", String)
    ], TeamCorrespondenceComponent.prototype, "teamId", void 0);
    __decorate([
        Watch("correspondence"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], TeamCorrespondenceComponent.prototype, "onCorrespondenceLoaded", null);
    __decorate([
        Watch("teamId"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], TeamCorrespondenceComponent.prototype, "onTeamChange", null);
    TeamCorrespondenceComponent = __decorate([
        Component({
            name: "TeamCorrespondenceComponent",
            components: {
                SectionLoader,
            },
        })
    ], TeamCorrespondenceComponent);
    return TeamCorrespondenceComponent;
})();
export default TeamCorrespondenceComponent;
